import React from 'react'
import PropTypes from 'prop-types'
import { isTestingMode } from '../../../../Testing'
import { LayoutEvents } from '../../../../Internals/LayoutEvents/LayoutEvents'

function StickyForTesting({ children }) {
  if (typeof children === 'function') {
    children = children(false, 'auto', false)
  }
  return (
    <div>
      <div>
        {children}
      </div>
    </div>
  )
}

class Sticky extends React.Component {
  _reflowing = false
  _scheduled = false
  _lastInnerHeight = -1

  static propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
    side: PropTypes.oneOf(['top', 'bottom']).isRequired,
    bottomAddition: PropTypes.number,
    getStop: PropTypes.func,
    offset: PropTypes.number,
    zIndex: PropTypes.number,
  }

  static defaultProps = {
    bottomAddition: 0,
    offset: 0,
    zIndex: 100,
    getStop: () => null,
  }

  constructor(props, context) {
    super(props, context)

    this.state = {
      fixed: false,
      height: -1,
      width: -1,
      left: 'auto',
      stoppedTop: false,
      relativeTop: 0,
    }
  }

  render() {
    let wrapperStyle = null
    let innerStyle = null
    const { zIndex } = this.props

    if (this.state.fixed) {
      if (this.state.stoppedTop) {
        innerStyle = {
          position: 'relative',
          zIndex,
          ...(this.state.stoppedTop ? { top: this.state.relativeTop } : {}),
        }
      } else {
        wrapperStyle = {
          height: this.state.height === -1 ? 'auto' : this.state.height,
          width: this.state.width === -1 ? 'auto' : this.state.width,
        }

        innerStyle = {
          left: this.state.left,
          position: 'fixed',
          zIndex,
        }

        if (this.props.side === 'top') {
          innerStyle.top = this.props.offset
        } else {
          innerStyle.bottom = this.props.offset
        }
      }
    }

    let { children } = this.props

    if (typeof children === 'function') {
      children = children(this.state.fixed, this.state.left, this.state.stoppedTop)
    }

    return (
      <div ref={this._refWrapper} style={wrapperStyle}>
        <div ref={this._refInner} style={innerStyle}>
          {children}
        </div>
      </div>
    )
  }

  _refWrapper = (ref) => {
    this._wrapper = ref
  }

  _refInner = (ref) => {
    this._inner = ref
  }

  componentDidMount() {
    this._reflow()

    this._layoutSubscription = LayoutEvents.addListener(() => this._reflow())
  }

  componentWillUnmount() {
    this._layoutSubscription.remove()
  }

  componentDidUpdate() {
    this._reflow()
  }

  _reflow = () => {
    if (this._reflowing) {
      this._scheduled = true

      return
    }

    this._scheduled = false
    this._reflowing = true
    const generator = this._doReflow()
    const check = () => {
      const next = generator.next()

      if (next.done) {
        this._reflowing = false
        if (this._scheduled) {
          this._reflow()
        }
      } else {
        this._setStateIfChanged(next.value, check)
      }
    }

    check()
  };

  * _doReflow() {
    const { documentElement } = document

    if (!documentElement) {
      throw Error('There is no "documentElement" in document')
    }

    const windowHeight = window.innerHeight || documentElement.clientHeight
    const wrapRect = this._wrapper.getBoundingClientRect()
    const wrapTop = wrapRect.top
    // 40 - padding
    const wrapBottom = wrapRect.bottom - (this.props.contentType === 'unit' ? 40 : 0)
    const wrapLeft = wrapRect.left
    const fixed = this.props.side.split(' ').some(side => {
        switch (side) {
          case 'top':
            return wrapTop < this.props.offset
          case 'bottom':
            return wrapBottom > windowHeight - this.props.offset
          default:
            return false
        }
      })

    const wasFixed = this.state.fixed

    if (fixed) {
      let { height, width } = this.state

      if (
        !wasFixed ||
        this._lastInnerHeight !== this._inner.offsetHeight ||
        this._lastInnerWidth !== this._inner.offsetWidth
      ) {
        yield {
          fixed: false,
          height,
          width,
        }
        height = this._inner.offsetHeight
        width = this._inner.offsetWidth
      }

      yield {
        width,
        height,
        fixed: true,
        left: wrapLeft,
      }

      this._lastInnerHeight = this._inner.offsetHeight
      this._lastInnerWidth = this._inner.offsetWidth

      const stop = this.props.getStop && this.props.getStop()

      if (stop) {
        const stopRect = stop.getBoundingClientRect()
        const outerHeight = height + this.props.offset
        const outerWidth = width + this.props.offset

        yield this.props.side.split(' ').reduce((newState, side) => {
            switch (side) {
              case 'top': {
                const stoppedTop = stopRect.top - outerHeight < 0
                const relativeTop = stopRect.top - height - wrapTop

                return {
                  ...newState,
                  relativeTop,
                  stoppedTop,
                }
              }
              case 'bottom': {
                const stoppedTop = stopRect.bottom + outerHeight + this.props.bottomAddition > windowHeight
                const relativeTop = stopRect.bottom - wrapTop + this.props.bottomAddition

                return {
                  ...newState,
                  relativeTop,
                  stoppedTop,
                }
              }
              default:
                return newState
            }
          }, {})
      }
    } else {
      yield { fixed: false, left: wrapLeft }
    }
  }

  _setStateIfChanged(state, callback) {
    for (const key in state) {
      if (this.state[key] !== state[key]) {
        this.setState(state, callback)

        return
      }
    }

    callback && callback()
  }
}

export default isTestingMode() ? StickyForTesting : Sticky;
